/**
 * NearbyMap — MapLibre GL JS + OpenFreeMap (free, no API key, OSM-based).
 *
 * Renders a vector basemap centered on the user's location. Falls back to
 * a default center (Honolulu) when geolocation is denied or unavailable.
 * Marker pins (deals, places) plug in via the optional `markers` prop.
 */

import { useEffect, useRef } from 'react';
import maplibregl, { Map as MlMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './NearbyMap.css';

const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const FALLBACK_CENTER: [number, number] = [-157.8294, 21.2793]; // Honolulu

export interface NearbyMapMarker {
  id: string;
  lng: number;
  lat: number;
  color?: string;
  onClick?: () => void;
}

interface Props {
  center?: { lat: number; lng: number } | null;
  zoom?: number;
  markers?: NearbyMapMarker[];
  /** When true, fit the viewport to the markers (overrides center/zoom). */
  fitToMarkers?: boolean;
  className?: string;
}

const NearbyMap: React.FC<Props> = ({
  center,
  zoom = 13,
  markers = [],
  fitToMarkers = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<Marker[]>([]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const c: [number, number] = center
      ? [center.lng, center.lat]
      : FALLBACK_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OPENFREEMAP_STYLE,
      center: c,
      zoom,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
    mapRef.current = map;
    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter when prop changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    map.flyTo({ center: [center.lng, center.lat], zoom, duration: 600 });
  }, [center, zoom]);

  // Sync markers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = markers.map(m => {
      const marker = new maplibregl.Marker({ color: m.color ?? '#aa12ff' })
        .setLngLat([m.lng, m.lat])
        .addTo(map);
      if (m.onClick) {
        marker.getElement().style.cursor = 'pointer';
        marker.getElement().addEventListener('click', m.onClick);
      }
      return marker;
    });

    // Frame the pins so they're visible even when the catalog results sit
    // outside the user-centered viewport (the dataset is catalog-wide, not
    // strictly local). Single pin → recenter; multiple → fit bounds.
    if (fitToMarkers && markers.length > 0) {
      if (markers.length === 1) {
        map.flyTo({
          center: [markers[0].lng, markers[0].lat],
          zoom,
          duration: 600,
        });
      } else {
        const bounds = new maplibregl.LngLatBounds();
        markers.forEach(m => bounds.extend([m.lng, m.lat]));
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 600 });
      }
    }
  }, [markers, fitToMarkers, zoom]);

  return (
    <div
      ref={containerRef}
      className={`wg-nearby-map${className ? ` ${className}` : ''}`}
    />
  );
};

export default NearbyMap;
